import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const ROUTINES = [
    {
        id: "morning", time: "5 min", title: "Start of day — posture check", to: "/",
        steps: [
            "Open Executive Overview and read the three gauges: Risk, Confidence, Clean Core.",
            "Scan the SoD Conflict Heatmap and the Top Toxic Roles widget for anything new.",
            "Check Verification Coverage — how much of your role library is field-verified rather than guessed.",
            "Jump to Findings and filter Critical + High; anything unassigned is today's work.",
        ],
    },
    {
        id: "intake", time: "10 min", title: "New engagement — intake & first analysis", to: "/workspaces",
        steps: [
            "Workspaces → create a workspace named after the client or system.",
            "Upload the exported artifacts (ABAP source, SU24/SU22 proposals, OData EDMX). No export handy? Use Paste Text.",
            "Run analysis — the three engines inspect everything and write a timeline snapshot.",
            "Review Findings, then open the Knowledge Graph tab to see how the objects connect.",
        ],
    },
    {
        id: "engineer", time: "15 min", title: "Engineer a role the secure way", to: "/roles",
        steps: [
            "Sentinel Forge → Role Library: paste specs as ROLE_NAME, TCODE;TCODE, description — one role per line.",
            "Or start from a template (Role Templates tab) or from real Fiori catalogs (Fiori Catalog Roles tab).",
            "Read the badges the moment the role appears: risk level, SoD conflicts, catalog coverage.",
            "Open Tools (wrench) → Field Editor and capture the REAL ACTVT / org-level values — this can clear a false conflict or confirm a real one.",
            "Use Simulate Addition before you add a T-code to a live role, never after.",
        ],
    },
    {
        id: "prove", time: "10 min", title: "Prove the access set is clean", to: "/roles",
        steps: [
            "Tick every role the user will hold, then press Simulate combined access.",
            "Cross-role conflicts are the ones no single role review would ever catch — treat them as findings.",
            "For each conflict, take the suggested mitigation control rather than inventing one.",
            "Press Visualize as Diagram to turn the toxic combination into a picture the business will understand.",
        ],
    },
    {
        id: "document", time: "10 min", title: "Document & hand over", to: "/roles",
        steps: [
            "Tools → Diagrams: generate the Three-Tier Role Model and the SoD Matrix, refine them in plain English if needed.",
            "Tools → BPD / PDD: generate the document, preview it, then download the .docx — the diagram is embedded.",
            "Export the .SAP reference file for the Basis team to import into PFCG.",
            "Export the workspace as PDF or PPTX for the steering committee.",
        ],
    },
    {
        id: "govern", time: "20 min / week", title: "Weekly governance pass", to: "/ruleset",
        steps: [
            "Ruleset Configuration: review the rule set, add or retire risks, read the data-quality banner honestly.",
            "Mitigation Library: keep controls current with what the client actually operates.",
            "Compliance Map: confirm the frameworks in scope are still the right ones.",
            "Compare → Timeline Diff: show the client how their posture moved since the last run.",
        ],
    },
    {
        id: "golive", time: "30 min", title: "Before go-live / audit", to: "/roles",
        steps: [
            "Run Migrate All to Fiori so no legacy T-code-only role goes live unmapped.",
            "Check Release Readiness on every role in scope (Tools → Release Readiness).",
            "Drive Verification Coverage as high as the evidence allows — auditors ask for values, not object names.",
            "Administration → audit trail: export the evidence that every change was human-approved.",
        ],
    },
];

/** Practical day-in-the-life reference: the routines a consultant repeats, with
 * the exact click-path for each one. */
export function UsageHandbook() {
    const nav = useNavigate();
    return (
        <Panel eyebrow="Daily Usage Handbook" title="How to actually work in UTSAVA Genesis">
            <p className="text-xs text-slate-500 font-mono mb-4">
                Seven routines that cover the whole working week — from the five-minute morning posture check
                to the go-live evidence pack.
            </p>
            <Accordion type="single" collapsible className="w-full" data-testid="about-usage-handbook">
                {ROUTINES.map((r) => (
                    <AccordionItem key={r.id} value={r.id} className="border-white/10">
                        <AccordionTrigger data-testid={`handbook-routine-${r.id}`} className="text-slate-200 hover:text-cyan-300 hover:no-underline">
                            <span className="flex items-center gap-3 text-left">
                                <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/25 shrink-0 inline-flex items-center gap-1">
                                    <Clock size={9} /> {r.time}
                                </span>
                                <span className="font-display text-sm">{r.title}</span>
                            </span>
                        </AccordionTrigger>
                        <AccordionContent>
                            <ol className="space-y-2 ml-1">
                                {r.steps.map((s, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                                        <span className="mt-0.5 shrink-0 w-5 h-5 rounded-sm bg-white/5 text-slate-300 border border-white/10 flex items-center justify-center font-mono text-[10px]">{i + 1}</span>
                                        {s}
                                    </li>
                                ))}
                            </ol>
                            <button
                                data-testid={`handbook-open-${r.id}`}
                                onClick={() => nav(r.to)}
                                className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors"
                            >
                                Start this routine <ArrowRight size={11} />
                            </button>
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </Panel>
    );
}
